/**
 * STATUS REGISTRY — every status color/label lives here, once, per entity.
 * Changing what "At Risk" looks like, or adding a new Creator status, is a
 * one-line edit here. No component may hardcode a status→color mapping;
 * components only call getStatusMeta(entity, key).
 */
export type Tone = 'good' | 'warn' | 'crit' | 'info' | 'neutral';

export interface StatusMeta {
  labelKey: string;
  tone: Tone;
}

export const STATUS_REGISTRY: Record<string, Record<string, StatusMeta>> = {
  creator: {
    new:         { labelKey: 'status.creator.new',         tone: 'neutral' },
    contacted:   { labelKey: 'status.creator.contacted',   tone: 'info' },
    interested:  { labelKey: 'status.creator.interested',  tone: 'info' },
    application: { labelKey: 'status.creator.application', tone: 'neutral' },
    pending_tiktok: { labelKey: 'status.creator.pendingTiktok', tone: 'warn' },
    joined:      { labelKey: 'status.creator.joined',      tone: 'good' },
    onboarding:  { labelKey: 'status.creator.onboarding',  tone: 'warn' },
    active:      { labelKey: 'status.creator.active',      tone: 'good' },
    at_risk:     { labelKey: 'status.creator.atRisk',      tone: 'crit' },
    inactive:    { labelKey: 'status.creator.inactive',    tone: 'neutral' },
    lost:        { labelKey: 'status.creator.lost',        tone: 'neutral' },
    rejected:    { labelKey: 'status.creator.rejected',    tone: 'crit' },
  },
  partnership: {
    active:      { labelKey: 'status.partnership.active',      tone: 'good' },
    pending:     { labelKey: 'status.partnership.pending',     tone: 'warn' },
    expired:     { labelKey: 'status.partnership.expired',     tone: 'crit' },
    terminated:  { labelKey: 'status.partnership.terminated',  tone: 'neutral' },
  },
  contract: {
    draft:            { labelKey: 'status.contract.draft',           tone: 'neutral' },
    under_review:     { labelKey: 'status.contract.underReview',     tone: 'info' },
    waiting_partner:  { labelKey: 'status.contract.waitingPartner',  tone: 'warn' },
    waiting_ugarits:  { labelKey: 'status.contract.waitingUgarits',  tone: 'warn' },
    ready:            { labelKey: 'status.contract.ready',           tone: 'info' },
    partially_signed: { labelKey: 'status.contract.partiallySigned', tone: 'warn' },
    fully_signed:     { labelKey: 'status.contract.fullySigned',     tone: 'good' },
    active:           { labelKey: 'status.contract.active',          tone: 'good' },
    expiring_soon:    { labelKey: 'status.contract.expiringSoon',    tone: 'warn' },
    expired:          { labelKey: 'status.contract.expired',         tone: 'crit' },
    terminated:       { labelKey: 'status.contract.terminated',      tone: 'neutral' },
    superseded:       { labelKey: 'status.contract.superseded',      tone: 'neutral' },
  },
  approval: {
    pending:  { labelKey: 'status.approval.pending',  tone: 'warn' },
    approved: { labelKey: 'status.approval.approved', tone: 'good' },
    rejected: { labelKey: 'status.approval.rejected', tone: 'crit' },
    expired:  { labelKey: 'status.approval.expired',  tone: 'neutral' },
    archived: { labelKey: 'status.approval.archived', tone: 'neutral' },
  },
  followup: {
    open: { labelKey: 'status.followup.open', tone: 'warn' },
    done: { labelKey: 'status.followup.done', tone: 'good' },
  },
  connector: {
    connected: { labelKey: 'status.connector.connected', tone: 'good' },
    pending:   { labelKey: 'status.connector.pending',   tone: 'warn' },
    awaiting:  { labelKey: 'status.connector.awaiting',  tone: 'info' },
    error:     { labelKey: 'status.connector.error',     tone: 'crit' },
    disabled:  { labelKey: 'status.connector.disabled',  tone: 'neutral' },
  },
  agent: {
    waiting_credentials: { labelKey: 'status.agent.waitingCredentials', tone: 'neutral' },
    configured_preview:  { labelKey: 'status.agent.configuredPreview',  tone: 'info' },
    idle:                { labelKey: 'status.agent.idle',               tone: 'info' },
    running:             { labelKey: 'status.agent.running',            tone: 'good' },
    paused:              { labelKey: 'status.agent.paused',             tone: 'warn' },
    error:               { labelKey: 'status.agent.error',              tone: 'crit' },
  },
  system: {
    not_connected:   { labelKey: 'status.system.notConnected',   tone: 'neutral' },
    operational:     { labelKey: 'status.system.operational',    tone: 'good' },
    degraded:        { labelKey: 'status.system.degraded',       tone: 'warn' },
    down:            { labelKey: 'status.system.down',           tone: 'crit' },
  },
  automation: {
    draft:             { labelKey: 'status.automation.draft',            tone: 'neutral' },
    waiting_connector: { labelKey: 'status.automation.waitingConnector', tone: 'warn' },
    paused:            { labelKey: 'status.automation.paused',           tone: 'warn' },
    archived:          { labelKey: 'status.automation.archived',         tone: 'neutral' },
  },
  ledgerPayment: {
    not_due:          { labelKey: 'status.ledger.notDue',         tone: 'neutral' },
    pending:          { labelKey: 'status.ledger.pending',        tone: 'warn' },
    approved:         { labelKey: 'status.ledger.approved',       tone: 'info' },
    partially_paid:   { labelKey: 'status.ledger.partiallyPaid',  tone: 'warn' },
    paid:             { labelKey: 'status.ledger.paid',           tone: 'good' },
    overdue:          { labelKey: 'status.ledger.overdue',        tone: 'crit' },
    disputed:         { labelKey: 'status.ledger.disputed',       tone: 'crit' },
  },
};

/** Permission levels are a status-like registry too — one place, not scattered strings. */
export const PERMISSION_LEVELS: Record<1 | 2 | 3, StatusMeta> = {
  1: { labelKey: 'permission.level1', tone: 'info' },
  2: { labelKey: 'permission.level2', tone: 'warn' },
  3: { labelKey: 'permission.level3', tone: 'crit' },
};

export function getStatusMeta(entity: keyof typeof STATUS_REGISTRY, key: string): StatusMeta {
  return STATUS_REGISTRY[entity]?.[key] ?? { labelKey: key, tone: 'neutral' };
}
